import { useRef, useState } from "react";
import { useTrackEvent } from "~/hooks/useTrackEvent";

type VslMeta = {
    vsl_id: string;
    video_name: string;
    video_person_name: string;
};

type VslTrackingOptions = {
    widget?: string;
    events?: {
        play: string;
        stop: string;
    };
    meta?: VslMeta;
};

const DEFAULT_VSL_META: VslMeta = {
    vsl_id: "dr_sarah_vsl",
    video_name: "Oestra – Dr Sarah Explainer",
    video_person_name: "Dr. Sarah Daccarett",
};

/**
 * Shared VSL (video sales letter) tracking for the "Meet Dr Sarah" video that
 * appears in the FounderExpert / ExpertFormulated sections. Owns the video ref,
 * play/pause state, and the play/stop analytics events (fired once per play,
 * with a stop on pause/complete). Return the handlers to wire onto a VideoPlayer.
 */
export const useVslTracking = ({
    widget = "meet_dr_sarah",
    events = { play: "vsl_play", stop: "vsl_stop" },
    meta = DEFAULT_VSL_META,
}: VslTrackingOptions = {}) => {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const hasPlayedRef = useRef(false);
    const sendEvent = useTrackEvent();

    const [videoStarted, setVideoStarted] = useState(false);

    const getVideoMeta = (completed = false) => {
        const video = videoRef.current;
        if (!video) return null;

        return {
            vsl_id: meta.vsl_id,
            video_name: meta.video_name,
            vsl_duration: video.duration,
            vsl_current_time: video.currentTime,
            vsl_completed: completed,
            vsl_sound: !video.muted,
            video_person_name: meta.video_person_name,
        };
    };

    const trackPlay = () => {
        if (hasPlayedRef.current) return;

        const metaData = getVideoMeta(false);
        if (!metaData) return;

        hasPlayedRef.current = true;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: events.play,
                widget,
                meta_data: metaData,
            },
        });
    };

    const trackStop = (completed = false) => {
        const metaData = getVideoMeta(completed);
        if (!metaData) return;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: events.stop,
                widget,
                meta_data: metaData,
            },
        });
    };

    const onTogglePlay = () => {
        const video = videoRef.current;
        if (!video) return;

        if (video.paused) {
            void video.play().catch(() => {});
        } else {
            video.pause();
        }
    };

    const onVideoPlay = () => {
        setVideoStarted(true);
        trackPlay();
    };

    const onVideoPause = () => {
        setVideoStarted(false);
        if (videoRef.current?.ended) return;
        trackStop(false);
    };

    const onVideoEnded = () => {
        setVideoStarted(false);
        trackStop(true);
    };

    return { videoRef, videoStarted, onTogglePlay, onVideoPlay, onVideoPause, onVideoEnded };
};

export default useVslTracking;
